/** Tick-based channels onto a regular axis: distance for the charts and the
 *  delta, the lap's own clock for the race-line map's position sync.
 */

import { column, required } from './columns';
import { axisGrid, interp, nearest } from './interp';
import { roundTo } from './round';
import type { Samples } from './types';

export const DEFAULT_STEP_M = 5;

/** Discrete per-tick columns (packed bitfields, and a gear with a sentinel)
 *  where linear interpolation would fabricate values that decode to nonsense
 *  — resampled with nearest-neighbour instead. For `aids` the nonsense is
 *  quiet: ASM coming on (0 → 2) interpolates through 1, which is the TCS bit,
 *  so every ASM onset read as a moment of traction control. For
 *  `suggested_gear` it is loud: halfway between a suggested third and no
 *  suggestion (15) is a suggested ninth (#108). */
export const NEAREST_COLUMNS: ReadonlySet<string> = new Set(['surface', 'aids', 'suggested_gear']);

/** Resample tick-based series onto a distance grid (see axisGrid). With no
 *  `columns`, or an empty list, every channel but `dist` is resampled. */
export function resampleByDistance(
  samples: Samples,
  step: number = DEFAULT_STEP_M,
  columns?: readonly string[],
): Samples {
  const dist = required(samples, 'dist');
  if (dist.length === 0) return { dist: [] };
  const grid = axisGrid(dist[dist.length - 1]!, step);
  const out: Samples = { dist: grid };
  const cols =
    columns !== undefined && columns.length > 0
      ? columns
      : Object.keys(samples).filter((k) => k !== 'dist');
  const m = grid.length;
  for (const col of cols) {
    const ys = required(samples, col);
    const values = new Array<number>(m);
    if (NEAREST_COLUMNS.has(col)) {
      for (let i = 0; i < m; i++) values[i] = nearest(dist, ys, grid[i]!);
    } else {
      for (let i = 0; i < m; i++) values[i] = roundTo(interp(dist, ys, grid[i]!), 4);
    }
    out[col] = values;
  }
  return out;
}

/** Resample tick-based series onto the lap's own clock, every `stepS`.
 *
 * The distance grid is the wrong axis for "where was this car after t
 * seconds": once a lap is lined up with a reference (alignment.ts) its
 * distance never runs backwards, so a spin or a rewind folds a stretch of
 * driving onto one point. The clock never folds.
 */
export function resampleByTime(samples: Samples, stepS: number, columns: readonly string[]): Samples {
  const t = column(samples, 't') ?? [];
  if (t.length === 0) return { t: [] };
  const grid = axisGrid(t[t.length - 1]!, stepS);
  const m = grid.length;
  const clock = new Array<number>(m);
  for (let i = 0; i < m; i++) clock[i] = roundTo(grid[i]!, 4);
  const out: Samples = { t: clock };
  for (const col of columns) {
    const ys = column(samples, col);
    if (ys === undefined) continue;
    const values = new Array<number>(m);
    for (let i = 0; i < m; i++) values[i] = roundTo(interp(t, ys, grid[i]!), 2);
    out[col] = values;
  }
  return out;
}
